import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BlockIcon from '@mui/icons-material/Block';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CourtEventApi, {
  ATTENDING_LABELS,
  CourtEventAttending,
} from '../../api/CourtEventApi';
import CoverageApi, { CoveringAttorneyDto } from '../../api/CoverageApi';

export interface AttendingControlProps {
  courtEventGuid?: string;
  value?: CourtEventAttending | null;
  coveringAttorneyId?: number | null;
  coveringAttorneyGuid?: string | null;
  coveringAttorneyName?: string | null;
  setBy?: string | null;
  setAt?: string | null;
  attorneys?: CoveringAttorneyDto[];
  isHearing?: boolean;
  disabled?: boolean;
  compact?: boolean;
  autoSave?: boolean;
  onChanged?: (
    attending: CourtEventAttending,
    coveringAttorneyId: number | null,
    coveringAttorneyGuid: string | null,
  ) => Promise<void> | void;
}

let cachedAttorneys: CoveringAttorneyDto[] | null = null;

export const AttendingControl: React.FC<AttendingControlProps> = ({
  courtEventGuid,
  value = 'NOT_SET',
  coveringAttorneyId = null,
  coveringAttorneyGuid = null,
  coveringAttorneyName = null,
  setBy = null,
  setAt = null,
  attorneys,
  isHearing = true,
  disabled = false,
  compact = false,
  autoSave = true,
  onChanged,
}) => {
  const currentAttending: CourtEventAttending = value || 'NOT_SET';
  const [attending, setAttending] = useState<CourtEventAttending>(currentAttending);
  const [selectedAttorneyGuid, setSelectedAttorneyGuid] = useState<string>(
    coveringAttorneyGuid || '',
  );
  const [availableAttorneys, setAvailableAttorneys] = useState<CoveringAttorneyDto[]>(
    attorneys || cachedAttorneys || [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAttending(value || 'NOT_SET');
  }, [value]);

  useEffect(() => {
    if (coveringAttorneyGuid) {
      setSelectedAttorneyGuid(coveringAttorneyGuid);
    }
  }, [coveringAttorneyGuid]);

  useEffect(() => {
    if (attorneys && attorneys.length > 0) {
      setAvailableAttorneys(attorneys);
      cachedAttorneys = attorneys;
    } else if (availableAttorneys.length === 0) {
      CoverageApi.listAttorneys()
        .then((list) => {
          setAvailableAttorneys(list);
          cachedAttorneys = list;
        })
        .catch((err) => console.error('Failed to load covering attorneys:', err));
    }
  }, [attorneys]);

  // If we have an attorney ID or name but not guid, try to resolve from available attorneys
  useEffect(() => {
    if (!selectedAttorneyGuid && availableAttorneys.length > 0) {
      if (coveringAttorneyName) {
        const match = availableAttorneys.find(
          (a) => a.CoveringAttorney_Name.toLowerCase() === coveringAttorneyName.toLowerCase(),
        );
        if (match) setSelectedAttorneyGuid(match.CoveringAttorney_guid);
      }
    }
  }, [availableAttorneys, coveringAttorneyName, selectedAttorneyGuid]);

  if (!isHearing) {
    return null;
  }

  const handleSelect = async (
    nextAttending: CourtEventAttending,
    attorneyGuid: string | null = null,
  ) => {
    if (disabled || saving) return;
    setError(null);

    const targetAttorneyGuid = nextAttending === 'COVERING_ATTORNEY' ? attorneyGuid : null;
    setAttending(nextAttending);
    if (nextAttending === 'COVERING_ATTORNEY') {
      setSelectedAttorneyGuid(targetAttorneyGuid || '');
    }

    if (autoSave && courtEventGuid) {
      setSaving(true);
      try {
        await CourtEventApi.updateCourtEvent({
          CourtEvent_guid: courtEventGuid,
          CourtEvent_Attending: nextAttending,
          CourtEvent_CoveringAttorney_guid: targetAttorneyGuid,
        });
      } catch (err) {
        console.error('Failed to update attending:', err);
        setError(err instanceof Error ? err.message : 'Failed to save attendance');
        setAttending(currentAttending);
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    onChanged?.(nextAttending, null, targetAttorneyGuid);
  };

  const handleAttorneyChange = async (event: SelectChangeEvent<string>) => {
    const guid = event.target.value;
    setSelectedAttorneyGuid(guid);
    await handleSelect('COVERING_ATTORNEY', guid);
  };

  const formatProvenance = () => {
    if (attending === 'NOT_SET') {
      return (
        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600 }}>
          Attendance not set yet — Lloyd's morning text will include this to be safe.
        </Typography>
      );
    }
    if (setBy) {
      const dateStr = setAt
        ? ` on ${new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
          }).format(new Date(setAt))}`
        : '';
      return (
        <Typography variant="caption" color="text.secondary">
          Set by {setBy}
          {dateStr}
        </Typography>
      );
    }
    return (
      <Typography variant="caption" color="text.secondary">
        Confirmed
      </Typography>
    );
  };

  if (compact) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Select
          size="small"
          value={attending}
          disabled={disabled || saving}
          onChange={(e) => handleSelect(e.target.value as CourtEventAttending, selectedAttorneyGuid)}
          sx={{
            minWidth: 150,
            fontSize: '0.85rem',
            bgcolor:
              attending === 'LLOYD'
                ? '#e8f5e9'
                : attending === 'COVERING_ATTORNEY'
                  ? '#e3f2fd'
                  : attending === 'NO_ONE'
                    ? '#ffebee'
                    : '#fff3e0',
            color:
              attending === 'LLOYD'
                ? '#1b5e20'
                : attending === 'COVERING_ATTORNEY'
                  ? '#0d47a1'
                  : attending === 'NO_ONE'
                    ? '#b71c1c'
                    : '#e65100',
            fontWeight: 600,
          }}
        >
          <MenuItem value="NOT_SET">Attendance not set</MenuItem>
          <MenuItem value="LLOYD">Lloyd Attending</MenuItem>
          <MenuItem value="COVERING_ATTORNEY">Covering Attorney</MenuItem>
          <MenuItem value="NO_ONE">No One Attending</MenuItem>
        </Select>

        {attending === 'COVERING_ATTORNEY' && (
          <Select
            size="small"
            displayEmpty
            value={selectedAttorneyGuid}
            disabled={disabled || saving}
            onChange={handleAttorneyChange}
            sx={{ minWidth: 160, fontSize: '0.85rem' }}
          >
            <MenuItem value="" disabled>
              <em>Select Attorney...</em>
            </MenuItem>
            {availableAttorneys.map((attorney) => (
              <MenuItem
                key={attorney.CoveringAttorney_guid}
                value={attorney.CoveringAttorney_guid}
              >
                {attorney.CoveringAttorney_Name}
              </MenuItem>
            ))}
          </Select>
        )}
        {saving && <CircularProgress size={16} />}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0.5, mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <ButtonGroup
          variant="outlined"
          size="small"
          disabled={disabled || saving}
          sx={{
            '& .MuiButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              px: 1.5,
              py: 0.5,
            },
          }}
        >
          <Button
            onClick={() => handleSelect('LLOYD')}
            variant={attending === 'LLOYD' ? 'contained' : 'outlined'}
            color="success"
            startIcon={<PersonIcon fontSize="small" />}
            sx={{
              fontWeight: attending === 'LLOYD' ? 700 : 500,
            }}
          >
            Lloyd
          </Button>

          <Button
            onClick={() => handleSelect('COVERING_ATTORNEY', selectedAttorneyGuid)}
            variant={attending === 'COVERING_ATTORNEY' ? 'contained' : 'outlined'}
            color="primary"
            startIcon={<GroupIcon fontSize="small" />}
            sx={{
              fontWeight: attending === 'COVERING_ATTORNEY' ? 700 : 500,
            }}
          >
            Covering Attorney
          </Button>

          <Button
            onClick={() => handleSelect('NO_ONE')}
            variant={attending === 'NO_ONE' ? 'contained' : 'outlined'}
            color="error"
            startIcon={<BlockIcon fontSize="small" />}
            sx={{
              fontWeight: attending === 'NO_ONE' ? 700 : 500,
            }}
          >
            No One
          </Button>
        </ButtonGroup>

        {attending === 'NOT_SET' && (
          <Chip
            size="small"
            label="Attendance Not Set"
            icon={<HelpOutlineIcon fontSize="small" />}
            sx={{
              bgcolor: '#fff3e0',
              color: '#d97706',
              fontWeight: 600,
              border: '1px solid #fcd34d',
            }}
          />
        )}

        {saving && <CircularProgress size={18} sx={{ ml: 1 }} />}
      </Stack>

      {attending === 'COVERING_ATTORNEY' && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              displayEmpty
              value={selectedAttorneyGuid}
              onChange={handleAttorneyChange}
              disabled={disabled || saving}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="" disabled>
                <em>Select Covering Attorney...</em>
              </MenuItem>
              {availableAttorneys.map((attorney) => (
                <MenuItem
                  key={attorney.CoveringAttorney_guid}
                  value={attorney.CoveringAttorney_guid}
                >
                  {attorney.CoveringAttorney_Name}
                  {attorney.CoveringAttorney_Notes ? ` (${attorney.CoveringAttorney_Notes})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedAttorneyGuid && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={
                availableAttorneys.find((a) => a.CoveringAttorney_guid === selectedAttorneyGuid)
                  ?.CoveringAttorney_Name || coveringAttorneyName || 'Attorney Selected'
              }
            />
          )}
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ mt: 0.5 }}>{formatProvenance()}</Box>
    </Box>
  );
};

export default AttendingControl;
